/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Enterprise-grade Reusable Data Table Component (Task 11.1.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useMemo } from 'react';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  Download,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { useI18n } from '../../i18n';
import { DataTablePagination } from './DataTablePagination';

export type SortDirection = 'asc' | 'desc' | null;

export interface ColumnDef<T> {
  id: string;
  header: string | ((props: { column: ColumnDef<T> }) => React.ReactNode);
  accessorKey?: keyof T;
  accessorFn?: (row: T) => any;
  cell?: (props: { row: T; value: any }) => React.ReactNode;
  enableSorting?: boolean;
  className?: string;
}

export interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  isLoading?: boolean;
  error?: string | null;
  totalCount?: number;
  pageIndex?: number;
  pageSize?: number;
  onPageChange?: (newPage: number) => void;
  onPageSizeChange?: (newSize: number) => void;
  isServerSide?: boolean;
  searchPlaceholder?: string;
  enableSearch?: boolean;
  onSearchChange?: (query: string) => void;
  enableCsvExport?: boolean;
  exportFileName?: string;
  className?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: { label: string; onClick: () => void };
}

/**
 * Exports data rows to CSV file with UTF-8 BOM encoding for complete Unicode/Vietnamese support
 */
export function exportToCsv<T>(columns: ColumnDef<T>[], data: T[], fileName = 'export.csv') {
  const exportableCols = columns.filter((col) => col.accessorKey || col.accessorFn);

  const headers = exportableCols.map((col) => {
    return typeof col.header === 'string' ? `"${col.header.replace(/"/g, '""')}"` : `"${col.id}"`;
  });

  const rows = data.map((row) => {
    return exportableCols
      .map((col) => {
        let val = '';
        if (col.accessorFn) {
          val = String(col.accessorFn(row) ?? '');
        } else if (col.accessorKey) {
          val = String(row[col.accessorKey] ?? '');
        }
        return `"${val.replace(/"/g, '""')}"`;
      })
      .join(',');
  });

  // Prepend UTF-8 BOM (\uFEFF) so Excel renders UTF-8 correctly
  const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\r\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  isLoading = false,
  error = null,
  totalCount,
  pageIndex = 0,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  isServerSide = false,
  searchPlaceholder,
  enableSearch = true,
  onSearchChange,
  enableCsvExport = true,
  exportFileName = 'admin_data_export.csv',
  className = '',
  emptyTitle,
  emptyDescription,
  emptyAction,
}: DataTableProps<T>) {
  const { t } = useI18n();

  const [sortColumnId, setSortColumnId] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientPageIndex, setClientPageIndex] = useState(0);
  const [clientPageSize, setClientPageSize] = useState(pageSize);

  const handleSort = (columnId: string, enabled?: boolean) => {
    if (enabled === false) return;

    if (sortColumnId !== columnId) {
      setSortColumnId(columnId);
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortColumnId(null);
      setSortDirection(null);
    }
  };

  const processedData = useMemo(() => {
    if (isServerSide) return data;

    let result = [...data];

    // Client-side search
    if (clientSearchQuery.trim()) {
      const q = clientSearchQuery.toLowerCase();
      result = result.filter((row) => {
        return columns.some((col) => {
          let val = '';
          if (col.accessorFn) val = String(col.accessorFn(row) ?? '');
          else if (col.accessorKey) val = String(row[col.accessorKey] ?? '');
          return val.toLowerCase().includes(q);
        });
      });
    }

    // Client-side sort
    if (sortColumnId && sortDirection) {
      const col = columns.find((c) => c.id === sortColumnId);
      if (col) {
        result.sort((a, b) => {
          let valA = col.accessorFn ? col.accessorFn(a) : col.accessorKey ? a[col.accessorKey] : '';
          let valB = col.accessorFn ? col.accessorFn(b) : col.accessorKey ? b[col.accessorKey] : '';

          if (valA === valB) return 0;
          if (valA === null || valA === undefined) return 1;
          if (valB === null || valB === undefined) return -1;

          const cmp = valA > valB ? 1 : -1;
          return sortDirection === 'asc' ? cmp : -cmp;
        });
      }
    }

    return result;
  }, [data, isServerSide, clientSearchQuery, sortColumnId, sortDirection, columns]);

  const activePageIndex = isServerSide ? pageIndex : clientPageIndex;
  const activePageSize = isServerSide ? pageSize : clientPageSize;
  const computedTotalCount = isServerSide ? (totalCount ?? data.length) : processedData.length;
  const computedPageCount = Math.ceil(computedTotalCount / activePageSize);

  const paginatedData = useMemo(() => {
    if (isServerSide) return processedData;
    const start = activePageIndex * activePageSize;
    return processedData.slice(start, start + activePageSize);
  }, [processedData, isServerSide, activePageIndex, activePageSize]);

  const handlePageChange = (newPage: number) => {
    if (isServerSide && onPageChange) {
      onPageChange(newPage);
    } else {
      setClientPageIndex(newPage);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    if (isServerSide && onPageSizeChange) {
      onPageSizeChange(newSize);
    } else {
      setClientPageSize(newSize);
      setClientPageIndex(0);
    }
  };

  const handleSearchInput = (val: string) => {
    if (isServerSide && onSearchChange) {
      onSearchChange(val);
    } else {
      setClientSearchQuery(val);
      setClientPageIndex(0);
    }
  };

  return (
    <div className={`flex flex-col border border-surface-border rounded-xl bg-surface overflow-hidden shadow-sm ${className}`}>
      {/* Table Toolbar */}
      {(enableSearch || enableCsvExport) && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 border-b border-surface-border bg-surface-elevated/40">
          {enableSearch && (
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-fg pointer-events-none" />
              <input
                type="text"
                placeholder={searchPlaceholder || t('table.filterPlaceholder')}
                onChange={(e) => handleSearchInput(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-sm rounded-lg border border-surface-border bg-surface text-foreground placeholder:text-muted-fg focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          )}

          {enableCsvExport && (
            <button
              type="button"
              onClick={() => exportToCsv(columns, processedData, exportFileName)}
              disabled={isLoading || processedData.length === 0}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-surface-border bg-surface hover:bg-surface-elevated text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors self-end sm:self-auto"
            >
              <Download className="w-3.5 h-3.5 text-muted-fg" />
              <span>{t('common.exportCsv')}</span>
            </button>
          )}
        </div>
      )}

      {/* Main Table View */}
      <div className="overflow-x-auto min-h-[240px]">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-surface-border bg-surface-elevated/80 text-xs font-semibold text-muted-fg uppercase tracking-wider">
              {columns.map((col) => {
                const isSorted = sortColumnId === col.id;
                const canSort = col.enableSorting !== false && (!!col.accessorKey || !!col.accessorFn);

                return (
                  <th
                    key={col.id}
                    className={`px-4 py-3 select-none ${col.className || ''} ${canSort ? 'cursor-pointer hover:text-foreground' : ''}`}
                    onClick={() => canSort && handleSort(col.id, col.enableSorting)}
                  >
                    <div className="flex items-center gap-1.5">
                      <span>
                        {typeof col.header === 'function' ? col.header({ column: col }) : col.header}
                      </span>
                      {canSort && (
                        <span className="text-muted-fg">
                          {isSorted && sortDirection === 'asc' ? (
                            <ArrowUp className="w-3 h-3 text-primary" />
                          ) : isSorted && sortDirection === 'desc' ? (
                            <ArrowDown className="w-3 h-3 text-primary" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-30 hover:opacity-100" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-border text-sm text-foreground">
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center text-muted-fg">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    <span>{t('common.loading')}</span>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center text-rose-500">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <AlertCircle className="w-6 h-6" />
                    <span>{error}</span>
                  </div>
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-16 text-center text-muted-fg">
                  <div className="flex flex-col items-center justify-center space-y-2 max-w-sm mx-auto">
                    <div className="size-10 rounded-2xl bg-surface-elevated border border-surface-border flex items-center justify-center text-muted-fg">
                      <Search className="w-5 h-5 opacity-40" />
                    </div>
                    <p className="text-sm font-semibold text-foreground">
                      {emptyTitle || t('table.noRecords')}
                    </p>
                    {emptyDescription && (
                      <p className="text-xs text-muted-fg">
                        {emptyDescription}
                      </p>
                    )}
                    {emptyAction && (
                      <button
                        type="button"
                        onClick={emptyAction.onClick}
                        className="mt-2 px-3.5 py-1.5 rounded-xl text-sm font-semibold bg-primary text-primary-fg hover:opacity-90 shadow-sm transition-all"
                      >
                        {emptyAction.label}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              paginatedData.map((row, rowIdx) => (
                <tr
                  key={row.id || rowIdx}
                  className="hover:bg-surface-elevated/60 transition-colors"
                >
                  {columns.map((col) => {
                    let cellValue: any = null;
                    if (col.accessorFn) {
                      cellValue = col.accessorFn(row);
                    } else if (col.accessorKey) {
                      cellValue = row[col.accessorKey];
                    }

                    return (
                      <td key={col.id} className={`px-4 py-3.5 align-middle ${col.className || ''}`}>
                        {col.cell ? col.cell({ row, value: cellValue }) : String(cellValue ?? '')}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <DataTablePagination
        pageIndex={activePageIndex}
        pageSize={activePageSize}
        totalCount={computedTotalCount}
        pageCount={computedPageCount}
        canPreviousPage={activePageIndex > 0}
        canNextPage={activePageIndex < computedPageCount - 1}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
}
